/**
 * ReminderDisplay Component
 * Displays a calm, black screen reminder with rose-colored text
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReminderDisplay.css';

const ReminderDisplay = ({ reminder, onDismiss, onSnooze, preferences }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Fade in
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(reminder.id);
    }, 300);
  };

  const handleSnooze = () => {
    setIsExiting(true);
    setTimeout(() => {
      onSnooze(reminder.id);
    }, 300);
  };

  const handleViewTasks = () => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/tasks');
      onDismiss(reminder.id);
    }, 300);
  };

  // Check if this is an overdue reminder
  const isOverdueReminder = reminder.related_entity_type === 'overdue_tasks';
  const overdueData = reminder.metadata || {};
  const overdueCount = overdueData.overdue_count || 0;
  const overdueTasks = overdueData.tasks || [];

  // Get message with interpolation
  const getMessage = () => {
    const message = reminder.message || 'Reminder';
    return message.replace('{{overdue_count}}', overdueCount);
  };

  const style = {
    backgroundColor: preferences?.style?.background_color || '#000000',
    opacity: isExiting ? 0 : (isVisible ? (preferences?.style?.opacity || 0.95) : 0),
  };

  const textStyle = {
    color: preferences?.style?.text_color || '#FFB6C1',
    fontSize: `${preferences?.style?.font_size || 16}px`,
  };

  return (
    <div 
      className={`reminder-overlay ${isVisible ? 'active' : ''}`}
      style={style}
      onClick={handleDismiss}
    >
      <div className="reminder-content" onClick={(e) => e.stopPropagation()}>
        <p className="reminder-message" style={textStyle}>
          {getMessage()}
        </p>
        
        {isOverdueReminder && overdueTasks.length > 0 && (
          <div className="overdue-task-list">
            {overdueTasks.map((task, index) => (
              <div key={index} className="overdue-task-item" style={{ borderColor: textStyle.color }}>
                <span className="task-title" style={textStyle}>{task.title}</span>
                <span className="task-late" style={textStyle}>
                  {task.days_late} {task.days_late === 1 ? 'day' : 'days'} late
                </span>
              </div>
            ))}
            {overdueCount > 5 && (
              <div className="more-tasks" style={textStyle}>
                +{overdueCount - 5} more task{overdueCount - 5 !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
        
        <div className="reminder-actions">
          {isOverdueReminder && (
            <button
              className="reminder-button primary"
              style={{ 
                backgroundColor: textStyle.color, 
                color: style.backgroundColor,
                borderColor: textStyle.color 
              }}
              onClick={handleViewTasks}
            >
              View Tasks
            </button>
          )}
          <button
            className="reminder-button"
            style={{ borderColor: textStyle.color, color: textStyle.color }}
            onClick={handleDismiss}
          >
            Dismiss
          </button>
          
          {reminder.config_id && (
            <button
              className="reminder-button"
              style={{ borderColor: textStyle.color, color: textStyle.color }}
              onClick={handleSnooze}
            >
              Later
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReminderDisplay;
